import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  useListCollectionImports,
  useUsersListNamesForIDs,
} from '@/api/trafikportalen';
import type { CollectionImportListItemDTO } from '@/api/trafikportalen';
import { useGetAllAdmins } from '@/hooks/useGetAllAdmins';
import { useImportStatusLabels } from '@/hooks/useImportStatusLabels';
import { Loading } from '@/components/Loading';
import { CollectionImportListItem } from './CollectionImportListItem';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { FilterPanel } from '@/components/FilterPanel';
import { VirtualizedList } from '@/components/VirtualizedList';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import type { ImportSummaryState } from '@/helpers/import';
import type { StudentType } from '@/types/students';

interface FormattedImportItem extends CollectionImportListItemDTO {
  creatorName: string;
}

interface CollectionImportListProps {
  collectionId: string;
  studentType: StudentType;
  tenantId: string;
}

export const CollectionImportList = ({
  collectionId,
  studentType,
  tenantId,
}: CollectionImportListProps) => {
  const navigate = useNavigate();
  const { data: imports, isLoading: importsLoading } =
    useListCollectionImports(collectionId);
  const { admins, isLoading: adminsLoading } = useGetAllAdmins();
  const { formatMessage } = useIntl();
  const statusLabels = useImportStatusLabels();
  const rightPanelRef = useRef<HTMLElement>(null);

  const [selectedCreators, setSelectedCreators] = useState<OptionsType[]>([]);
  const [selectedActions, setSelectedActions] = useState<OptionsType[]>([]);
  const [announcement, setAnnouncement] = useState('');

  const adminMap = useMemo(() => {
    const map = new Map<string, string>();
    admins.forEach((admin) => {
      map.set(admin.id, `${admin.firstname} ${admin.lastname}`);
    });
    return map;
  }, [admins]);

  const unknownCreatorIds = useMemo(() => {
    if (!imports) return '';
    const ids = [
      ...new Set(
        imports
          .map((item) => item.created_by)
          .filter((id) => !adminMap.has(id)),
      ),
    ];
    return ids.join(',');
  }, [imports, adminMap]);

  const { data: fetchedUserNames } = useUsersListNamesForIDs(
    { ids: unknownCreatorIds },
    {
      query: {
        enabled: !adminsLoading && unknownCreatorIds.length > 0,
      },
    },
  );

  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    fetchedUserNames?.forEach((u) => {
      map.set(u.id, `${u.firstname} ${u.lastname}`);
    });
    return map;
  }, [fetchedUserNames]);

  const formattedImports = useMemo((): FormattedImportItem[] => {
    if (!imports) return [];
    return [...imports]
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .map((item) => ({
        ...item,
        creatorName:
          adminMap.get(item.created_by) ??
          userNameMap.get(item.created_by) ??
          formatMessage({
            defaultMessage: 'Okänd skapare (behörighet saknas eller borttagen)',
          }),
      }));
  }, [imports, adminMap, userNameMap, formatMessage]);

  const creatorOptions = useMemo((): OptionsType[] => {
    const seen = new Map<string, string>();
    formattedImports.forEach((item) => {
      if (!seen.has(item.created_by)) {
        seen.set(item.created_by, item.creatorName);
      }
    });
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [formattedImports]);

  const actionOptions = useMemo(
    (): OptionsType[] =>
      Object.entries(statusLabels).map(([id, label]) => ({ id, label })),
    [statusLabels],
  );

  const filteredImports = useMemo(() => {
    let items = formattedImports;

    if (selectedCreators.length > 0) {
      const creatorIds = new Set(selectedCreators.map((c) => c.id));
      items = items.filter((item) => creatorIds.has(item.created_by));
    }

    if (selectedActions.length > 0) {
      const actionIds = selectedActions.map((a) => a.id as ImportSummaryState);
      items = items.filter((item) =>
        actionIds.some((action) => item.summary[action] > 0),
      );
    }

    return items;
  }, [formattedImports, selectedCreators, selectedActions]);

  const handleImportClick = useCallback(
    (importId: string) => {
      navigate({
        to: `/$tenantId/students/${studentType}/imports/$importId`,
        params: { tenantId, importId },
      });
    },
    [navigate, tenantId, studentType],
  );

  const renderImportItem = useCallback(
    (item: FormattedImportItem) => {
      return (
        <CollectionImportListItem
          key={item.import_id}
          importItem={item}
          creatorName={item.creatorName}
          onClick={() => handleImportClick(item.import_id)}
        />
      );
    },
    [handleImportClick],
  );

  useEffect(() => {
    const count = filteredImports.length;
    setAnnouncement(
      formatMessage(
        {
          defaultMessage:
            '{count, plural, one {# import} other {# importer}} visas',
        },
        { count },
      ),
    );
  }, [filteredImports, formatMessage]);

  if (importsLoading || adminsLoading) {
    return <Loading />;
  }

  return (
    <FilteredListContainer>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel ref={rightPanelRef}>
        <FilterPanel
          firstInput={
            <Dropdown
              label={formatMessage({ defaultMessage: 'Skapare' })}
              id="creator-filter"
              options={creatorOptions}
              placeholder={formatMessage({ defaultMessage: 'Alla' })}
              value={selectedCreators}
              onValueChange={setSelectedCreators}
              multiSelect
              containerRef={rightPanelRef}
            />
          }
          filterOptions={[
            <Dropdown
              label={formatMessage({ defaultMessage: 'Åtgärdstyp' })}
              id="action-filter"
              options={actionOptions}
              placeholder={formatMessage({ defaultMessage: 'Alla' })}
              value={selectedActions}
              onValueChange={setSelectedActions}
              multiSelect
              containerRef={rightPanelRef}
            />,
          ]}
        />
      </RightPanel>
      <VirtualizedList
        count={filteredImports.length}
        countName={formatMessage({ defaultMessage: 'importer' })}
        totalItems={formattedImports}
        filteredItems={filteredImports}
        renderItem={renderImportItem}
        getItemKey={(item) => item.import_id}
      />
    </FilteredListContainer>
  );
};
